import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import {
  Plus, Trash2, ChevronDown, ChevronRight, BookOpen, Target,
  BarChart3, X, Users, Clock, CheckCircle, Pencil, PlusCircle, Save
} from 'lucide-react';

const CourseProgress = () => {
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isTeacher = user?.role === 'teacher';

  const [plans, setPlans] = useState([]);
  const [reports, setReports] = useState([]);
  const [teacherReports, setTeacherReports] = useState(null);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(isTeacher ? 'my-progress' : 'report');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState({});

  // Create plan form
  const [newPlan, setNewPlan] = useState({ course_id: '', course_name: '', subjects: [] });
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newTopic, setNewTopic] = useState({ name: '', required_classes: 1, estimated_hours: '', teacher_id: '', teacher_name: '' });
  const [editingSubjectIdx, setEditingSubjectIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, reportsRes] = await Promise.all([
        api.getCourseProgressPlans(),
        api.getCourseProgressReport()
      ]);
      setPlans(plansRes.data.plans || []);
      setReports(reportsRes.data.reports || []);

      if (isTeacher) {
        const myRes = await api.getMyProgress();
        setTeacherReports(myRes.data);
      }
    } catch { /* ignore */ }

    try {
      const [coursesRes, subsRes, teachersRes] = await Promise.all([
        api.getCoursesList(),
        api.getSubjects(),
        api.getTeachers()
      ]);
      setCourses(coursesRes.data || []);
      setSubjects(subsRes.data || []);
      setTeachers(teachersRes.data?.filter(t => t.role === 'teacher') || teachersRes.data || []);
    } catch { /* ignore */ }

    setLoading(false);
  }, [isTeacher]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCourseSelect = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    setNewPlan({ ...newPlan, course_id: courseId, course_name: course?.name || '' });
  };

  const addSubjectToPlan = () => {
    if (!newSubjectName.trim()) return;
    if (newPlan.subjects.find(s => s.subject_name.toLowerCase() === newSubjectName.trim().toLowerCase())) {
      toast.error('Subject already added');
      return;
    }
    setNewPlan({
      ...newPlan,
      subjects: [...newPlan.subjects, { subject_name: newSubjectName.trim(), topics: [] }]
    });
    setNewSubjectName('');
  };

  const addTopicToSubject = (subjectIdx) => {
    if (!newTopic.name.trim() || newTopic.required_classes < 1) return;
    const updated = [...newPlan.subjects];
    updated[subjectIdx].topics.push({
      name: newTopic.name.trim(),
      required_classes: parseInt(newTopic.required_classes),
      estimated_hours: newTopic.estimated_hours ? parseFloat(newTopic.estimated_hours) : null,
      teacher_id: newTopic.teacher_id || null,
      teacher_name: newTopic.teacher_name || null
    });
    setNewPlan({ ...newPlan, subjects: updated });
    setNewTopic({ name: '', required_classes: 1, estimated_hours: '', teacher_id: '', teacher_name: '' });
  };

  const removeTopicFromNewPlan = (subjectIdx, topicIdx) => {
    const updated = [...newPlan.subjects];
    updated[subjectIdx].topics.splice(topicIdx, 1);
    if (updated[subjectIdx].topics.length === 0) updated.splice(subjectIdx, 1);
    setNewPlan({ ...newPlan, subjects: updated });
  };

  const handleCreatePlan = async () => {
    if (!newPlan.course_id || newPlan.subjects.length === 0) {
      toast.error('Select a course and add at least one subject with topics');
      return;
    }
    const hasTopics = newPlan.subjects.every(s => s.topics.length > 0);
    if (!hasTopics) {
      toast.error('Every subject must have at least one topic');
      return;
    }
    setSubmitting(true);
    try {
      await api.createCourseProgressPlan(newPlan);
      toast.success('Course plan created');
      setShowCreateModal(false);
      setNewPlan({ course_id: '', course_name: '', subjects: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create plan');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit modal with existing plan data
  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setNewPlan({
      course_id: plan.course_id,
      course_name: plan.course_name,
      subjects: (plan.subjects || []).map(s => ({
        subject_name: s.subject_name,
        topics: (s.topics || []).map(t => ({
          name: t.name,
          required_classes: t.required_classes,
          estimated_hours: t.estimated_hours || null,
          teacher_id: t.teacher_id || null,
          teacher_name: t.teacher_name || null
        }))
      }))
    });
    setShowEditModal(true);
  };

  const handleSaveEditPlan = async () => {
    if (newPlan.subjects.length === 0) {
      toast.error('Add at least one subject with topics');
      return;
    }
    const hasTopics = newPlan.subjects.every(s => s.topics.length > 0);
    if (!hasTopics) {
      toast.error('Every subject must have at least one topic');
      return;
    }
    setSubmitting(true);
    try {
      await api.updateCourseProgressPlan(editingPlan.id, { subjects: newPlan.subjects });
      toast.success('Plan updated');
      setShowEditModal(false);
      setEditingPlan(null);
      setNewPlan({ course_id: '', course_name: '', subjects: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCourse = async () => {
    if (!newCourseName.trim()) return;
    try {
      await api.createCourse({ name: newCourseName.trim() });
      toast.success('Course added');
      setNewCourseName('');
      setShowAddCourseModal(false);
      // Refresh courses list
      const res = await api.getCoursesList();
      setCourses(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add course');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this course plan?')) return;
    try {
      await api.deleteCourseProgressPlan(planId);
      toast.success('Plan deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDeleteTopic = async (planId, topicId) => {
    if (!window.confirm('Remove this topic?')) return;
    try {
      await api.removeTopicFromPlan(planId, topicId);
      toast.success('Topic removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove topic');
    }
  };

  const toggleSubject = (planId, subjectName) => {
    const key = `${planId}-${subjectName}`;
    setExpandedSubject(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ProgressBar = ({ percent, className = '' }) => (
    <div className={`h-2.5 bg-slate-100 rounded-full overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          percent >= 100 ? 'bg-emerald-500' : percent >= 50 ? 'bg-blue-500' : percent > 0 ? 'bg-amber-500' : 'bg-slate-200'
        }`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Loading course progress...</div>;
  }

  return (
    <div data-testid="course-progress-page" className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-wide">Course Progress Report</h1>
          <p className="text-slate-500 text-sm">Track subject-wise and topic-wise coverage for each course</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              data-testid="add-course-btn"
              onClick={() => setShowAddCourseModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition font-medium text-sm border border-slate-300"
            >
              <PlusCircle size={16} /> Add Course
            </button>
            <button
              data-testid="create-plan-btn"
              onClick={() => { setEditingPlan(null); setNewPlan({ course_id: '', course_name: '', subjects: [] }); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              <Plus size={18} /> Create Course Plan
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {!isTeacher && (
          <button
            data-testid="tab-report"
            onClick={() => setTab('report')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              tab === 'report' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <BarChart3 size={16} className="inline mr-1.5 -mt-0.5" /> Progress Report
          </button>
        )}
        {isTeacher && (
          <button
            data-testid="tab-my-progress"
            onClick={() => setTab('my-progress')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              tab === 'my-progress' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Target size={16} className="inline mr-1.5 -mt-0.5" /> My Progress
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-plans"
            onClick={() => setTab('plans')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              tab === 'plans' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <BookOpen size={16} className="inline mr-1.5 -mt-0.5" /> Manage Plans
          </button>
        )}
      </div>

      {/* Tab: Progress Report */}
      {tab === 'report' && (
        <div className="space-y-6">
          {reports.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              <BarChart3 size={40} className="mx-auto mb-2 opacity-30" />
              <p>No course plans yet. Create a course plan to see progress.</p>
            </div>
          ) : reports.map(report => (
            <div key={report.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Course Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{report.course_name}</h2>
                  <p className="text-sm text-slate-500">{report.total_completed}/{report.total_required} classes completed</p>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-bold ${report.overall_percent >= 100 ? 'text-emerald-600' : report.overall_percent >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                    {report.overall_percent}%
                  </p>
                  <p className="text-xs text-slate-400">Overall</p>
                </div>
              </div>
              <ProgressBar percent={report.overall_percent} className="mx-6 mt-3" />

              {/* Subjects */}
              <div className="p-6 space-y-4">
                {report.subjects.map(subj => {
                  const key = `${report.id}-${subj.subject_name}`;
                  const isExpanded = expandedSubject[key];
                  return (
                    <div key={subj.subject_name} className="border border-slate-100 rounded-lg">
                      <button
                        onClick={() => toggleSubject(report.id, subj.subject_name)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <span className="font-medium text-slate-800">{subj.subject_name}</span>
                          <span className="text-xs text-slate-400">{subj.completed_classes}/{subj.required_classes} classes</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-32">
                            <ProgressBar percent={subj.percent} />
                          </div>
                          <span className={`text-sm font-bold w-14 text-right ${subj.percent >= 100 ? 'text-emerald-600' : subj.percent >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                            {subj.percent}%
                          </span>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-3 border-t border-slate-100">
                          <table className="w-full text-sm mt-2">
                            <thead>
                              <tr className="text-xs text-slate-400 uppercase">
                                <th className="text-left py-2">Topic</th>
                                <th className="text-center py-2">Required</th>
                                <th className="text-center py-2">Completed</th>
                                <th className="text-center py-2">Teacher</th>
                                <th className="text-right py-2">Coverage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subj.topics.map(topic => (
                                <tr key={topic.id || topic.name} className="border-t border-slate-50">
                                  <td className="py-2 text-slate-700">{topic.name}</td>
                                  <td className="py-2 text-center text-slate-500">{topic.required_classes}</td>
                                  <td className="py-2 text-center">
                                    <span className={topic.completed_classes >= topic.required_classes ? 'text-emerald-600 font-medium' : 'text-slate-600'}>
                                      {topic.completed_classes}
                                    </span>
                                  </td>
                                  <td className="py-2 text-center text-xs text-slate-400">{topic.teacher_name || '-'}</td>
                                  <td className="py-2 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-16">
                                        <ProgressBar percent={topic.percent} />
                                      </div>
                                      <span className={`text-xs font-medium w-10 text-right ${topic.percent >= 100 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                        {topic.percent}%
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab: My Progress (Teacher) */}
      {tab === 'my-progress' && teacherReports && (
        <div className="space-y-6">
          {teacherReports.reports?.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-slate-400">
              <Target size={40} className="mx-auto mb-2 opacity-30" />
              <p>No courses assigned to you yet.</p>
            </div>
          ) : teacherReports.reports?.map((r, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">{r.course_name}</h2>
                <span className={`text-2xl font-bold ${r.overall_percent >= 100 ? 'text-emerald-600' : 'text-blue-600'}`}>{r.overall_percent}%</span>
              </div>
              <ProgressBar percent={r.overall_percent} className="mb-4" />
              {r.subjects.map(s => (
                <div key={s.subject_name} className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{s.subject_name}</span>
                    <span className="text-xs text-slate-500">{s.completed_classes}/{s.required_classes} — {s.percent}%</span>
                  </div>
                  <ProgressBar percent={s.percent} className="mb-2" />
                  <div className="ml-4 space-y-1">
                    {s.topics.map(t => (
                      <div key={t.name} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{t.name}</span>
                        <span className={t.percent >= 100 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
                          {t.completed_classes}/{t.required_classes} ({t.percent}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tab: Manage Plans */}
      {tab === 'plans' && isAdmin && (
        <div className="space-y-4">
          {plans.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-slate-400">
              <BookOpen size={40} className="mx-auto mb-2 opacity-30" />
              <p>No course plans. Click "Create Course Plan" to get started.</p>
            </div>
          ) : plans.map(plan => (
            <div key={plan.id} className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div>
                  <button onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)} className="flex items-center gap-2">
                    {expandedPlan === plan.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <h3 className="text-lg font-semibold text-slate-900">{plan.course_name}</h3>
                  </button>
                  <p className="text-xs text-slate-400 ml-6">
                    {plan.subjects?.length || 0} subjects, {plan.subjects?.reduce((a, s) => a + (s.topics?.length || 0), 0)} topics
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`edit-plan-${plan.id}`}
                    onClick={() => openEditPlan(plan)}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                    title="Edit Plan"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`delete-plan-${plan.id}`}
                    onClick={() => handleDeletePlan(plan.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                    title="Delete Plan"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedPlan === plan.id && (
                <div className="p-6">
                  {plan.subjects?.map(subj => (
                    <div key={subj.subject_name} className="mb-4">
                      <h4 className="font-medium text-slate-700 mb-2">{subj.subject_name}</h4>
                      <div className="ml-4 space-y-1">
                        {subj.topics?.map(topic => (
                          <div key={topic.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-50">
                            <span className="text-slate-600">{topic.name}</span>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span>{topic.required_classes} classes</span>
                              {topic.estimated_hours && <span>{topic.estimated_hours}h</span>}
                              {topic.teacher_name && <span>{topic.teacher_name}</span>}
                              <button onClick={() => handleDeleteTopic(plan.id, topic.id)} className="text-red-400 hover:text-red-600">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-plan-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Create Course Plan</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Course *</label>
                <select
                  data-testid="plan-course-select"
                  value={newPlan.course_id}
                  onChange={e => handleCourseSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select course</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Add Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Add Subject</label>
                <div className="flex gap-2">
                  <select
                    value={newSubjectName}
                    onChange={e => setNewSubjectName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Select or type subject</option>
                    {subjects.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  <input
                    type="text"
                    value={newSubjectName}
                    onChange={e => setNewSubjectName(e.target.value)}
                    placeholder="Or type new name"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <button onClick={addSubjectToPlan} className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium">
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Subjects & Topics */}
              {newPlan.subjects.map((subj, sIdx) => (
                <div key={sIdx} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">{subj.subject_name}</h3>
                    <button onClick={() => {
                      const updated = [...newPlan.subjects];
                      updated.splice(sIdx, 1);
                      setNewPlan({ ...newPlan, subjects: updated });
                    }} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>

                  {/* Existing topics */}
                  {subj.topics.map((t, tIdx) => (
                    <div key={tIdx} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
                      <span>{t.name}</span>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span>{t.required_classes} classes</span>
                        {t.teacher_name && <span>{t.teacher_name}</span>}
                        <button onClick={() => removeTopicFromNewPlan(sIdx, tIdx)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                      </div>
                    </div>
                  ))}

                  {/* Add topic form */}
                  {editingSubjectIdx === sIdx ? (
                    <div className="mt-3 space-y-2 p-3 bg-slate-50 rounded-lg">
                      <input
                        type="text"
                        value={newTopic.name}
                        onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                        placeholder="Topic name"
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-slate-500">Required Classes *</label>
                          <input
                            type="number" min="1"
                            value={newTopic.required_classes}
                            onChange={e => setNewTopic({ ...newTopic, required_classes: e.target.value })}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Est. Hours</label>
                          <input
                            type="number" step="0.5"
                            value={newTopic.estimated_hours}
                            onChange={e => setNewTopic({ ...newTopic, estimated_hours: e.target.value })}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Teacher</label>
                          <select
                            value={newTopic.teacher_id}
                            onChange={e => {
                              const t = teachers.find(x => x.id === e.target.value);
                              setNewTopic({ ...newTopic, teacher_id: e.target.value, teacher_name: t?.name || '' });
                            }}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
                          >
                            <option value="">Any</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { addTopicToSubject(sIdx); }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium"
                        >Add Topic</button>
                        <button onClick={() => setEditingSubjectIdx(null)} className="px-3 py-1.5 text-slate-500 text-xs">Done</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingSubjectIdx(sIdx); setNewTopic({ name: '', required_classes: 1, estimated_hours: '', teacher_id: '', teacher_name: '' }); }}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Plus size={12} /> Add Topic
                    </button>
                  )}
                </div>
              ))}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button
                  data-testid="submit-plan-btn"
                  onClick={handleCreatePlan}
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditModal && editingPlan && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-plan-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Edit Plan: {editingPlan.course_name}</h2>
              <button onClick={() => { setShowEditModal(false); setEditingPlan(null); }} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Add Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Add Subject</label>
                <div className="flex gap-2">
                  <select
                    value={newSubjectName}
                    onChange={e => setNewSubjectName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Select subject</option>
                    {subjects.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  <input
                    type="text"
                    value={newSubjectName}
                    onChange={e => setNewSubjectName(e.target.value)}
                    placeholder="Or type new name"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <button onClick={addSubjectToPlan} className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium">
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Subjects & Topics */}
              {newPlan.subjects.map((subj, sIdx) => (
                <div key={sIdx} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">{subj.subject_name}</h3>
                    <button onClick={() => {
                      const updated = [...newPlan.subjects];
                      updated.splice(sIdx, 1);
                      setNewPlan({ ...newPlan, subjects: updated });
                    }} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>

                  {subj.topics.map((t, tIdx) => (
                    <div key={tIdx} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
                      <span>{t.name}</span>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span>{t.required_classes} classes</span>
                        {t.estimated_hours && <span>{t.estimated_hours}h</span>}
                        {t.teacher_name && <span>{t.teacher_name}</span>}
                        <button onClick={() => removeTopicFromNewPlan(sIdx, tIdx)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                      </div>
                    </div>
                  ))}

                  {editingSubjectIdx === sIdx ? (
                    <div className="mt-3 space-y-2 p-3 bg-slate-50 rounded-lg">
                      <input
                        type="text"
                        value={newTopic.name}
                        onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                        placeholder="Topic name"
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-slate-500">Required Classes *</label>
                          <input type="number" min="1" value={newTopic.required_classes}
                            onChange={e => setNewTopic({ ...newTopic, required_classes: e.target.value })}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Est. Hours</label>
                          <input type="number" step="0.5" value={newTopic.estimated_hours}
                            onChange={e => setNewTopic({ ...newTopic, estimated_hours: e.target.value })}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Teacher</label>
                          <select value={newTopic.teacher_id}
                            onChange={e => {
                              const t = teachers.find(x => x.id === e.target.value);
                              setNewTopic({ ...newTopic, teacher_id: e.target.value, teacher_name: t?.name || '' });
                            }}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white">
                            <option value="">Any</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => addTopicToSubject(sIdx)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium">Add Topic</button>
                        <button onClick={() => setEditingSubjectIdx(null)} className="px-3 py-1.5 text-slate-500 text-xs">Done</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingSubjectIdx(sIdx); setNewTopic({ name: '', required_classes: 1, estimated_hours: '', teacher_id: '', teacher_name: '' }); }}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                      <Plus size={12} /> Add Topic
                    </button>
                  )}
                </div>
              ))}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => { setShowEditModal(false); setEditingPlan(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button
                  data-testid="save-edit-plan-btn"
                  onClick={handleSaveEditPlan}
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save size={16} />
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Course Modal */}
      {showAddCourseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="add-course-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Add New Course</h2>
              <button onClick={() => setShowAddCourseModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Course Name</label>
                <input
                  data-testid="new-course-name-input"
                  type="text"
                  value={newCourseName}
                  onChange={e => setNewCourseName(e.target.value)}
                  placeholder="e.g., SSC CGL 2026"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={e => e.key === 'Enter' && handleAddCourse()}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowAddCourseModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button
                  data-testid="submit-add-course-btn"
                  onClick={handleAddCourse}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Add Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseProgress;
